interface IFetch {
  path: string
  method: string
  urlParams?: Record<string, string>
  body?: unknown
  headers?: Record<string, string>
  isFile?: boolean
  isDownloadFile?: boolean
}

export class FetchClient {
  private API_URL = import.meta.env.VITE_API_BASE_URL || ''
  private activeTenantId: string | null = import.meta.env.TENANT_ID || import.meta.env.VITE_TENANT_ID || null

  constructor(private defaultHeaders: Record<string, string> = {}) {}

  public setActiveTenantId(tenantId: string | null) {
    this.activeTenantId = tenantId ?? null
  }

  public setApiBaseUrl(apiBaseUrl: string | null) {
    this.API_URL = apiBaseUrl ?? ''
  }

  public getActiveTenantId() {
    return this.activeTenantId
  }

  private buildQuery(params?: Record<string, unknown>): string {
    if (!params) return ''

    const toParamValue = (value: unknown) => {
      if (value instanceof Date) {
        return value.toISOString()
      }
      return String(value)
    }

    const searchParams = new URLSearchParams()

    for (const [key, value] of Object.entries(params)) {
      if (value == null) continue

      if (typeof value === 'number' && !Number.isFinite(value)) continue

      if (typeof value === 'string' && value.trim() === '') continue

      if (Array.isArray(value)) {
        let appendedAny = false
        for (const item of value) {
          if (item == null) continue
          if (typeof item === 'number' && !Number.isFinite(item)) continue
          if (typeof item === 'string' && item.trim() === '') continue
          searchParams.append(key, toParamValue(item))
          appendedAny = true
        }

        if (!appendedAny) continue
        continue
      }

      searchParams.append(key, toParamValue(value))
    }

    const queryString = searchParams.toString()
    return queryString ? `?${queryString}` : ''
  }

  private replaceUrlParams(path: string, urlParams?: Record<string, string>): string {
    if (!urlParams) return path
    return Object.entries(urlParams).reduce((acc, [key, val]) => acc.replace(`:${key}`, encodeURIComponent(val)), path)
  }

  async get<T>(
    path: string,
    query?: Record<string, unknown>,
    urlParams?: Record<string, string>,
    headers?: Record<string, string>,
    isDownloadFile = false,
  ): Promise<T> {
    path = this.replaceUrlParams(path, urlParams) + this.buildQuery(query)
    return this.fetch<T>({
      path,
      method: 'GET',
      headers,
      isDownloadFile,
    })
  }

  async post<T>(
    path: string,
    body?: unknown,
    urlParams?: Record<string, string>,
    headers?: Record<string, string>,
    isFile: boolean = false,
    isDownloadFile: boolean = false,
  ): Promise<T> {
    path = this.replaceUrlParams(path, urlParams)

    return this.fetch<T>({
      path,
      method: 'POST',
      body,
      headers,
      isFile,
      isDownloadFile,
    })
  }

  async put<T>(
    path: string,
    body?: unknown,
    urlParams?: Record<string, string>,
    headers?: Record<string, string>,
  ): Promise<T> {
    path = this.replaceUrlParams(path, urlParams)
    return this.fetch<T>({
      path,
      method: 'PUT',
      body,
      headers,
    })
  }

  async delete<T>(
    path: string,
    body?: unknown,
    urlParams?: Record<string, string>,
    headers?: Record<string, string>,
  ): Promise<T> {
    path = this.replaceUrlParams(path, urlParams)
    return this.fetch<T>({
      path,
      method: 'DELETE',
      body,
      headers,
    })
  }

  async patch<T>(
    path: string,
    body?: unknown,
    urlParams?: Record<string, string>,
    headers?: Record<string, string>,
  ): Promise<T> {
    path = this.replaceUrlParams(path, urlParams)
    return this.fetch<T>({
      path,
      method: 'PATCH',
      body,
      headers,
    })
  }

  private async fetch<T>({ path, method, body, headers = {}, isFile, isDownloadFile }: IFetch): Promise<T> {
    const url = /^https?:\/\//i.test(path) ? path : `${this.API_URL}${path}`
    const _headers = { ...headers }

    // Attach content-type / accept depending on whether this is a file upload
    if (!isFile) _headers['Content-Type'] = 'application/json'
    else _headers['Accept'] = 'application/json'

    const tenantId = this.getActiveTenantId()
    const hasTenantHeader = Object.keys(_headers).some((key) => key.toLowerCase() === 'x-tenant-id')
    if (tenantId && !hasTenantHeader) {
      _headers['X-Tenant-ID'] = tenantId
    }

    const response = await fetch(url, {
      method: method,
      headers: {
        ...this.defaultHeaders,
        ..._headers,
      },
      body: body ? (isFile ? (body as FormData) : JSON.stringify(body)) : null,
    })

    if ([400, 403, 500].includes(response.status)) {
      const data = await response.json()
      const error = new Error(JSON.stringify(data.detail))
      ;(error as Error & { cause?: unknown }).cause = data
      throw error
    }

    if (!response.ok) {
      const data = await response.json()
      const error = new Error(JSON.stringify(data?.title))
      ;(error as Error & { cause?: unknown }).cause = data
      throw error
    }

    if (isDownloadFile) {
      return {
        filename: decodeURIComponent(response.headers.get('content-disposition')?.split("filename*=UTF-8''")[1] ?? ''),
        data: await response.blob(),
      } as T
    }

    return response.json()
  }
}

export const $fetch = new FetchClient()
